import {
  SIGN_IN_ERRORS,
  type ConfirmAction,
  type ConfirmSelectionOrganization,
  type Facility,
} from "@/frontend/types/delete-patient-data";

/**
 * Returns checked account ids for facilities that can still be changed.
 * @param organization Workspace facilities shown to the patient.
 * @param selected Account ids checked by the patient.
 * @returns Checked account ids that are not already scheduled for deletion.
 */
export function getSelectedAccountIds(
  organization: {
    facilities: Array<Pick<Facility, "accountId" | "deletionScheduledAt">>;
  },
  selected: ReadonlySet<string>,
): string[] {
  return organization.facilities
    .filter((facility) => !facility.deletionScheduledAt && selected.has(facility.accountId))
    .map((facility) => facility.accountId);
}

/**
 * Derives the narrowest truthful scope from selected authorized facility rows.
 * Names always come from the rendered server response rather than caller input.
 *
 * @returns A confirmation action, or null when no authorized row is selected.
 */
export function deriveConfirmAction(
  kind: ConfirmAction["kind"],
  organizations: ConfirmSelectionOrganization[],
  selected: ReadonlySet<string>,
): ConfirmAction | null {
  const authorized = organizations.flatMap((organization) =>
    organization.facilities
      .filter((facility) => !facility.deletionScheduledAt)
      .map((facility) => ({ facility, organization })),
  );
  const selectedRows = authorized.filter(({ facility }) => selected.has(facility.accountId));
  if (selectedRows.length === 0) return null;

  if (selectedRows.length === 1) {
    const selectedRow = selectedRows[0];
    if (!selectedRow) return null;
    const { facility, organization } = selectedRow;
    return {
      kind,
      scope: "one",
      facilityName: facility.sourceName,
      organizationName: organization.organizationName,
    };
  }

  if (selectedRows.length === authorized.length) {
    return { kind, scope: "all" };
  }

  const selectedOrganizationIds = new Set(selectedRows.map(({ organization }) => organization.organizationId));
  if (selectedOrganizationIds.size === 1) {
    const firstSelectedRow = selectedRows[0];
    if (!firstSelectedRow) return null;
    const organization = firstSelectedRow.organization;
    const actionableCount = organization.facilities.filter((facility) => !facility.deletionScheduledAt).length;
    if (selectedRows.length === actionableCount) {
      return { kind, scope: "organization", organizationName: organization.organizationName };
    }
  }

  return { kind, scope: "selection", selectedCount: selectedRows.length };
}

/**
 * Builds the title, body and button label for a pending action. Every
 * destructive path on this page is described in full before it runs — the
 * patient has to be able to tell revoke from delete without guessing.
 *
 * The wait before the rows are actually purged comes from the server, so the
 * dialog never promises a grace period the deployment does not apply.
 *
 * @param action The action awaiting confirmation.
 * @param graceDays How long the purge waits. Zero deletes at once.
 * @returns The three strings the dialog renders.
 */
export function confirmActionCopy(
  action: ConfirmAction,
  graceDays: number,
): {
  title: string;
  body: string;
  confirm: string;
} {
  // "after N days" is a promise; at zero there is no wait to promise.
  const wait =
    graceDays === 0
      ? ""
      : ` Everything is fully erased after ${graceDays} ${graceDays === 1 ? "day" : "days"}.`;
  const facility =
    action.facilityName && action.organizationName
      ? `${action.facilityName} in ${action.organizationName}`
      : "this health system";
  const workspace = action.organizationName ?? "this workspace";

  if (action.kind === "revoke") {
    if (action.scope === "all") {
      return {
        title: "Stop all access",
        body: "This destroys the access we hold for every health system listed here. No one can retrieve anything new from them. Records already retrieved are kept. Use Delete to remove those.",
        confirm: "Stop all access",
      };
    }
    if (action.scope === "organization") {
      return {
        title: `Stop access in ${workspace}`,
        body: `This destroys the access we hold for every health system in ${workspace}. Other workspaces are left alone. Records already retrieved are kept. Use Delete to remove those.`,
        confirm: "Stop access",
      };
    }
    if (action.scope === "selection") {
      const count = action.selectedCount;
      const subject = count ? `${count} selected health systems` : "the selected health systems";
      return {
        title: "Stop selected access",
        body: `This destroys the access we hold for ${subject}. No one can retrieve anything new from them. Records already retrieved are kept. Use Delete to remove those.`,
        confirm: "Stop selected access",
      };
    }
    return {
      title: "Stop access",
      body: `This destroys the access we hold for ${facility}. No one can retrieve anything new from it. Records already retrieved are kept. Use Delete to remove those.`,
      confirm: "Stop access",
    };
  }

  if (action.scope === "all") {
    return {
      title: "Delete everything",
      body: `This permanently removes all records from every health system listed here and stops any further retrieval immediately.${wait}`,
      confirm: "Delete everything",
    };
  }
  if (action.scope === "organization") {
    return {
      title: `Delete records in ${workspace}`,
      body: `This permanently removes all records from every health system in ${workspace} and stops any further retrieval immediately. Other workspaces are left alone.${wait}`,
      confirm: "Delete records",
    };
  }
  if (action.scope === "selection") {
    const count = action.selectedCount;
    const subject = count ? `${count} selected health systems` : "the selected health systems";
    return {
      title: "Delete selected records",
      body: `This permanently removes all records from ${subject} and stops any further retrieval immediately.${wait}`,
      confirm: "Delete selected records",
    };
  }
  return {
    title: "Delete these records",
    body: `This permanently removes all records from ${facility} and stops any further retrieval immediately.${wait}`,
    confirm: "Delete records",
  };
}

/** Renders a date the way a patient reads one, or an em-space when absent. */
export function formatFacilityDate(value: Date | null): string {
  if (!value) return "—";
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(value);
}

/**
 * Whether a facility can still be acted on. One already scheduled for deletion
 * has nothing left to revoke or delete, so it cannot be ticked.
 */
export function isFacilityActionable(facility: Facility): boolean {
  return !facility.deletionScheduledAt;
}

/** The `?error=` code the OAuth routes redirect back with, if any. */
export function signInErrorFromUrl(): string | null {
  const code = new URLSearchParams(window.location.search).get("error");
  if (!code) return null;
  return SIGN_IN_ERRORS[code.toUpperCase()] ?? "We couldn't complete that sign-in. Please try again.";
}
